import React from "react";
import { Box, Heading, Text, SimpleGrid, Spinner, Alert, AlertIcon, Button } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useSkillDevelopment } from "../../../hooks/useSkillDevelopment";

const getCardData = (skillData) => [
    {
        icon: (
            <img src="/png/total-coursr.icon.png" alt="Total Course" width="48" height="48" />
        ),
        value: skillData?.totalCourse?.toString() || "0",
        label: "Total Course",
    },
    {
        icon: (
            <img src="/png/register-trainee-icon.png" alt="Registered Trainee" width="48" height="48" />
        ),
        value: skillData?.registeredTrainee?.toString() || "0",
        label: "Registered Trainee",
    },
    {
        icon: (
            <img src="/png/achived-cirtification-icon.png" alt="Achieved Certification" width="48" height="48" />
        ),
        value: skillData?.achievedCertification?.toString() || "0",
        label: "Achieved Certification",
    },
];

const Card = ({ icon, value, label }) => (
    <Box
        bg="white"
        borderRadius={{ base: "12px", md: "16px" }}
        boxShadow="0 8px 32px rgba(35,91,99,0.10), 0 1.5px 6px rgba(35,91,99,0.08)"
        p={{ base: 4, sm: 5, md: 6, lg: 8 }}
        minH={{ base: "120px", sm: "130px", md: "140px" }}
        position="relative"
        overflow="hidden"
        display="flex"
        alignItems="center"
        flexDirection={{ base: "column", sm: "row" }}
        textAlign={{ base: "center", sm: "left" }}
    >
        {/* Top-right SVG */}
        <Box position="absolute" top={0} right={0} zIndex={1} display={{ base: "none", sm: "block" }}>
            <svg width="80" height="60" viewBox="0 0 80 60" fill="none">
                <path d="M80 0L50 60" stroke="#E6F2F3" strokeWidth="4" />
            </svg>
        </Box>
        {/* Bottom-left SVG */}
        <Box position="absolute" bottom={0} left={0} zIndex={1} display={{ base: "none", sm: "block" }}>
            <svg width="80" height="60" viewBox="0 0 80 60" fill="none">
                <path d="M0 60L30 0" stroke="#E6F2F3" strokeWidth="4" />
            </svg>
        </Box>
        <Box 
            zIndex={2} 
            mr={{ base: 0, sm: 3, md: 5 }} 
            ml={{ base: 0, sm: 1 }} 
            mb={{ base: 2, sm: 0 }}
            display="flex" 
            alignItems="center"
            fontSize={{ base: "2xl", sm: "3xl", md: "3xl" }}
        >
            {icon}
        </Box>
        <Box zIndex={2} display="flex" flexDirection="column" alignItems={{ base: "center", sm: "flex-start" }}>
            <Text 
                fontSize={{ base: "xl", sm: "xl", md: "2xl" }} 
                fontWeight="bold" 
                color="#235B63" 
                mb={0} 
                lineHeight={1}
            >
                {value}
            </Text>
            <Text 
                fontSize={{ base: "sm", sm: "sm", md: "md" }} 
                color="#235B63" 
                fontWeight="500" 
                mt={1}
            >
                {label}
            </Text>
        </Box>
    </Box>
);

const SkillDevelopmentHero = () => {
    const navigate = useNavigate();
    const { skillData, loading, error } = useSkillDevelopment();
    
    // Enhanced debugging for the Hero component
    React.useEffect(() => {
        }, [skillData, loading, error]);
    
    const cardData = getCardData(skillData);
    
    // Debug card data generation
    return (
        <Box
            bg="#235B63"
            minH={{ base: "80vh", sm: "70vh", md: "60vh", lg: "50vh" }}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            pt={{ base: 16, sm: 12, md: 15, lg: 10 }}
            pb={{ base: 8, sm: 10, md: 15 }}
            px={{ base: 4, sm: 6, md: 8 }}
            position="relative"
            bgImage="url('/png/career-header.png')"
            bgSize={{ base: "cover", md: "contain" }}
            bgPosition={{ base: "center", md: "contain" }}
            bgRepeat="no-repeat"
            overflow="hidden"
        >
            {/* Login/Sign-Up Button - Top Right */}
            <Button
                variant="outline"
                borderColor="#5A9BD4"
                color="#5A9BD4"
                borderRadius="full"
                px={{ base: 4, md: 6 }}
                py={{ base: 2, md: 3 }}
                fontWeight="600"
                fontSize={{ base: "sm", md: "md" }}
                _hover={{ bg: "#5A9BD4", color: "white" }}
                onClick={() => window.location.href = `https://ba-systems.com/course/dashboard`}
                position="absolute"
                top={{ base: 4, sm: 6, md: 8 }}
                right={{ base: 4, sm: 6, md: 8 }}
                zIndex={10}
                display={{ base: "block", xs: "block" }}
                whiteSpace="nowrap"
                minW="fit-content"
            >
                <Text display={{ base: "none", sm: "block" }}>Login / Sign-Up</Text>
                <Text display={{ base: "block", sm: "none" }}>Login</Text>
            </Button>

            <Heading
                as="h1"
                fontSize={{ base: "2xl", sm: "3xl", md: "4xl", lg: "5xl" }}
                fontWeight="bold"
                color="#E6FF2B"
                mb={{ base: 2, md: 4 }}
                zIndex={2}
                textAlign="center"
                lineHeight={{ base: 1.2, md: 1.1 }}
                px={{ base: 2, md: 0 }}
            >
                Skill Development
            </Heading>
            <Text
                fontSize={{ base: "md", sm: "lg", md: "xl", lg: "2xl" }}
                color="white"
                mb={{ base: 6, sm: 8, md: 12 }}
                zIndex={2}
                textAlign="center"
                lineHeight={{ base: 1.4, md: 1.3 }}
                px={{ base: 2, md: 0 }}
            >
                Grow your abilities grow your career
            </Text>
            
            {loading ? (
                <Box zIndex={2} textAlign="center" w="100%">
                    <Spinner size={{ base: "lg", md: "xl" }} color="white" />
                    <Text color="white" mt={4} fontSize={{ base: "sm", md: "md" }}>
                        Loading skill development data...
                    </Text>
                </Box>
            ) : (
                <>
                    {error && (
                        <Box zIndex={2} w={{ base: "95%", sm: "90%", md: "70%" }} mb={4}>
                            <Alert status="warning" borderRadius="md" bg="rgba(255,255,255,0.9)" fontSize="sm">
                                <AlertIcon />
                                {error}
                            </Alert>
                        </Box>
                    )}
                    <SimpleGrid 
                        columns={{ base: 1, sm: 1, md: 2, lg: 3 }} 
                        spacing={{ base: 3, sm: 4, md: 4, lg: 4 }} 
                        w={{ base: "100%", sm: "95%", md: "90%", lg: "85%", xl: "80%" }} 
                        zIndex={2}
                        maxW="1200px"
                    >
                        {cardData.map((card, idx) => (
                            <Box key={idx} h="auto" minH="40px" py={{ base: 1, md: 2 }}>
                                <Card {...card} />
                            </Box>
                        ))}
                    </SimpleGrid>
                </>
            )}
        </Box>
    );
};

export default SkillDevelopmentHero; 